import { Button } from '../../components/Button';
import { Checkbox } from '../../components/Checkbox';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { ChevronBackIcon } from '../../icons/ChevronBackIcon';
import {
  BackChip,
  BackRow,
  DetailField,
  DetailLabel,
  DetailValue,
  Footer,
  HeaderActions,
  HeaderCard,
  ItemBody,
  ItemCard,
  ItemDetailsGrid,
  ItemHeader,
  ItemImage,
  ItemImagePlaceholder,
  ItemImageWrapper,
  ItemPolicyHint,
  ItemPrice,
  ItemPriceColumn,
  ItemSelectionRow,
  ItemSubPrice,
  ItemTitle,
  ItemsList,
  PageWrapper,
  StatusPill,
  SummaryField,
  SummaryLabel,
  SummaryRow,
  SummaryValue,
  Title,
} from './OrderDetailTemplate.styles';
import type {
  OrderDetailItem,
  OrderDetailTemplateLabels,
  OrderDetailTemplateProps,
} from './OrderDetailTemplate.types';

const SKELETON_ITEM_COUNT = 2;
const SKELETON_DETAIL_FIELDS = 6;

const renderSkeletonHeader = (loadingLabel: string) => (
  <HeaderCard aria-busy="true" aria-label={loadingLabel}>
    <SkeletonBlock variant="heading" width="60%" />
    <SkeletonBlock width="80px" height="20px" borderRadius="9999px" />
    <SummaryRow>
      {Array.from({ length: 4 }, (_, index) => (
        <SummaryField key={`summary-skeleton-${index}`}>
          <SkeletonBlock variant="text" width="60%" />
          <SkeletonBlock variant="text" width="80%" />
        </SummaryField>
      ))}
    </SummaryRow>
  </HeaderCard>
);

const renderSkeletonItem = (index: number, loadingLabel: string) => (
  <ItemCard key={`item-skeleton-${index}`} aria-busy="true" aria-label={loadingLabel}>
    <ItemImageWrapper>
      <SkeletonBlock width="100%" height="100%" />
    </ItemImageWrapper>
    <ItemBody>
      <ItemHeader>
        <SkeletonBlock variant="heading" width="55%" />
        <ItemPriceColumn>
          <SkeletonBlock variant="text" width="80px" />
          <SkeletonBlock variant="text" width="120px" />
        </ItemPriceColumn>
      </ItemHeader>
      <ItemDetailsGrid>
        {Array.from({ length: SKELETON_DETAIL_FIELDS }, (_, fieldIndex) => (
          <DetailField key={`detail-skeleton-${index}-${fieldIndex}`}>
            <SkeletonBlock variant="text" width="40%" />
            <SkeletonBlock variant="text" width="70%" />
          </DetailField>
        ))}
      </ItemDetailsGrid>
    </ItemBody>
  </ItemCard>
);

type RenderItemArgs = {
  item: OrderDetailItem;
  labels: OrderDetailTemplateLabels;
  selectionMode: boolean;
  isSelected: boolean;
  onToggle: () => void;
};

const renderItem = ({ item, labels, selectionMode, isSelected, onToggle }: RenderItemArgs) => {
  const isSelectable = item.selectable !== false;

  return (
    <ItemCard
      key={item.id}
      aria-label={item.title}
      data-selectable={selectionMode && !isSelectable ? 'false' : undefined}
    >
      <ItemImageWrapper>
        {item.imageUrl ? (
          <ItemImage src={item.imageUrl} alt={item.imageAlt ?? item.title} />
        ) : (
          <ItemImagePlaceholder>{labels.imagePlaceholder}</ItemImagePlaceholder>
        )}
      </ItemImageWrapper>
      <ItemBody>
        {selectionMode && (
          <ItemSelectionRow>
            <Checkbox
              checked={isSelected}
              onChange={onToggle}
              disabled={!isSelectable}
              label={item.selectionLabel}
              aria-label={
                item.selectionLabel ? `${item.selectionLabel}: ${item.title}` : item.title
              }
            />
          </ItemSelectionRow>
        )}
        <ItemHeader>
          <ItemTitle>{item.title}</ItemTitle>
          <ItemPriceColumn>
            <ItemPrice>{item.price}</ItemPrice>
            {item.subPrice && <ItemSubPrice>{item.subPrice}</ItemSubPrice>}
          </ItemPriceColumn>
        </ItemHeader>
        {selectionMode && item.policyHint && (
          <ItemPolicyHint>
            <span>{item.policyHint.line1}</span>
            {item.policyHint.line2 && <span>{item.policyHint.line2}</span>}
          </ItemPolicyHint>
        )}
        {item.details.length > 0 && (
          <ItemDetailsGrid>
            {item.details.map((field, index) => (
              <DetailField key={`${item.id}-detail-${index}`}>
                <DetailLabel>{field.label}</DetailLabel>
                <DetailValue>{field.value}</DetailValue>
              </DetailField>
            ))}
          </ItemDetailsGrid>
        )}
      </ItemBody>
    </ItemCard>
  );
};

export function OrderDetailTemplate({
  title,
  reference,
  status,
  summaryFields,
  items,
  labels,
  onBack,
  onAmend,
  onCancel,
  onAddToBooking,
  isLoading = false,
  selectionMode = false,
  selectedItemIds,
  onSelectionChange,
  onConfirmSelection,
  onExitSelection,
}: OrderDetailTemplateProps) {
  const showHeaderActions = Boolean(onAmend || onCancel);
  const loadingLabel = labels.loading ?? 'Loading booking';

  if (isLoading) {
    return (
      <PageWrapper data-testid="order-detail-template" aria-busy="true">
        {onBack && (
          <BackRow>
            <BackChip type="button" onClick={onBack} aria-label={labels.back}>
              <ChevronBackIcon />
            </BackChip>
          </BackRow>
        )}

        {renderSkeletonHeader(loadingLabel)}

        <ItemsList>
          {Array.from({ length: SKELETON_ITEM_COUNT }, (_, index) =>
            renderSkeletonItem(index, loadingLabel)
          )}
        </ItemsList>
      </PageWrapper>
    );
  }

  const selectedSet = new Set(selectedItemIds ?? []);
  const handleToggle = (itemId: string) => {
    if (!onSelectionChange) return;
    if (selectedSet.has(itemId)) {
      onSelectionChange((selectedItemIds ?? []).filter((id) => id !== itemId));
    } else {
      onSelectionChange([...(selectedItemIds ?? []), itemId]);
    }
  };

  return (
    <PageWrapper data-testid="order-detail-template">
      {onBack && !selectionMode && (
        <BackRow>
          <BackChip type="button" onClick={onBack} aria-label={labels.back}>
            <ChevronBackIcon />
          </BackChip>
        </BackRow>
      )}

      <HeaderCard aria-label={title}>
        <Title>{title}</Title>
        <StatusPill data-status={status}>{labels.statuses[status]}</StatusPill>

        <SummaryRow>
          {summaryFields.map((field, index) => (
            <SummaryField key={`summary-${index}-${reference}`}>
              <SummaryLabel>{field.label}</SummaryLabel>
              <SummaryValue>{field.value}</SummaryValue>
            </SummaryField>
          ))}
          {selectionMode ? (
            <HeaderActions>
              {onExitSelection && (
                <Button type="button" variant="outline" onClick={onExitSelection}>
                  {labels.exitSelection ?? labels.back}
                </Button>
              )}
              {onConfirmSelection && (
                <Button
                  type="button"
                  variant="danger"
                  onClick={onConfirmSelection}
                  disabled={selectedSet.size === 0}
                >
                  {labels.confirmSelection ?? labels.cancel}
                </Button>
              )}
            </HeaderActions>
          ) : (
            showHeaderActions && (
              <HeaderActions>
                {onAmend && (
                  <Button type="button" variant="outline" onClick={onAmend}>
                    {labels.amend}
                  </Button>
                )}
                {onCancel && (
                  <Button type="button" variant="primary" onClick={onCancel}>
                    {labels.cancel}
                  </Button>
                )}
              </HeaderActions>
            )
          )}
        </SummaryRow>
      </HeaderCard>

      <ItemsList>
        {items.map((item) =>
          renderItem({
            item,
            labels,
            selectionMode,
            isSelected: selectedSet.has(item.id),
            onToggle: () => handleToggle(item.id),
          })
        )}
      </ItemsList>

      {onAddToBooking && !selectionMode && (
        <Footer>
          <Button type="button" variant="primary" size="large" onClick={onAddToBooking}>
            {labels.addToBooking}
          </Button>
        </Footer>
      )}
    </PageWrapper>
  );
}
